import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import {
  Box,
  CircularProgress,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";

import { RiLinkM, RiLockLine } from "react-icons/ri";
import DownloadIcon from "../../../../icons/DownloadIcon";

import { ClaimAppealLetter } from "../../../../../types/claims";
import { chatService } from "../../../../../services/chatService";
import type { AppealLetterFormat } from "../../../../../services/apiService";
import { saveAppealLetter } from "../../../../../services/fileDownloadService";
import { useCapabilities } from "../../../../../contexts/permissions/useCapabilities";
import useNotifier from "../../../../../hooks/useNotifier";
import DocumentEditor from "../../../../ui/document-editor/DocumentEditor";
import CoreSelect from "../../../../ui/core/CoreSelect";

// The backend answers these when the viewer can't see the chat the letter lives in
const isNoAccessStatus = (status?: number) => status === 403 || status === 404;

// Names the owner only when every letter belongs to the same known user
const PrivateChatNotice: FC<{ letters: ClaimAppealLetter[] }> = ({
  letters,
}) => {
  const owners = new Set(letters.map((l) => l.generatedBy || l.generatedByEmail));
  const [owner] = owners;
  const isPlural = letters.length > 1;
  return (
    <Box
      sx={{
        m: 2,
        p: 2,
        display: "flex",
        gap: 1.5,
        alignItems: "flex-start",
        backgroundColor: "grey.50",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "10px",
      }}
    >
      <Box sx={{ color: "text.secondary", pt: 0.25 }}>
        <RiLockLine size={18} />
      </Box>
      <Box>
        <Typography sx={{ fontSize: "0.875rem", fontWeight: 600 }}>
          {isPlural
            ? "These letters are in private chats"
            : "This letter is in a private chat"}
        </Typography>
        <Typography sx={{ fontSize: "0.8125rem", color: "text.secondary" }}>
          {owners.size === 1 && owner
            ? `Only ${owner} can open ${isPlural ? "them" : "it"}.`
            : isPlural
              ? "Only the chats' owners can open them."
              : "Only the chat's owner can open it."}
        </Typography>
      </Box>
    </Box>
  );
};

/** One letter version: the latest save in one of the claim's letter chats. `number`
 * is its position across all of those chats, oldest first. */
type LetterVersion = ClaimAppealLetter & { number: number };

const generatedTime = (letter: ClaimAppealLetter) => {
  const t = new Date(letter.generatedAt).getTime();
  return isNaN(t) ? 0 : t;
};

/**
 * Read-only view of the claim's appeal letter in the Claim timeline: one version per
 * letter chat, the newest openable one by default, plus a download and a link back to
 * the chat the selected version came from. Versions in chats the viewer can't open
 * stay in the picker and show the private-chat notice instead of the letter.
 */
const AppealLetterDetail: FC<{
  /** The timeline card's letter (the first generated one) */
  letter: ClaimAppealLetter;
  /** All of the claim's letters, one per chat */
  letters: ClaimAppealLetter[];
  claimNumber?: string;
}> = ({ letter, letters, claimNumber }) => {
  const navigate = useNavigate();
  const notifier = useNotifier();
  const { canDownloadAttachments } = useCapabilities();

  // Stable memo dependency; the letters array may be rebuilt per render
  const lettersKey = letters
    .map((l) => `${l.chatId}:${l.available}:${l.generatedAt}`)
    .join(",");

  // Number oldest first across chats, then list newest first
  const versions = useMemo<LetterVersion[]>(
    () =>
      [...letters]
        .sort((a, b) => generatedTime(a) - generatedTime(b))
        .map((l, i) => ({ ...l, number: i + 1 }))
        .reverse(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [lettersKey],
  );

  const [selectedChatIdState, setSelectedChatId] = useState<string | null>(
    null,
  );
  const [content, setContent] = useState<string | null>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Chats whose letter answered 403/404 although appealLetters marked it available
  const [deniedChatIds, setDeniedChatIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [downloadingFormat, setDownloadingFormat] =
    useState<AppealLetterFormat | null>(null);

  // Falls back to the newest openable version until one is picked, or if the pick goes away
  const selectedVersion =
    versions.find((v) => v.chatId === selectedChatIdState) ??
    versions.find((v) => v.available) ??
    versions[0] ??
    null;
  const selectedChatId = selectedVersion?.chatId ?? null;
  const isSelectedLocked =
    !selectedVersion ||
    !selectedVersion.available ||
    deniedChatIds.has(selectedVersion.chatId);

  useEffect(() => {
    if (selectedChatId === null || isSelectedLocked) return;
    let cancelled = false;

    setIsLoadingContent(true);
    setError(null);
    chatService.getLatestChatOutputDocument(selectedChatId).then((response) => {
      if (cancelled) return;
      if (response.success && response.data) {
        setContent(response.data.content ?? "");
      } else if (isNoAccessStatus(response.status)) {
        setDeniedChatIds((prev) => new Set(prev).add(selectedChatId));
      } else {
        setContent(null);
        setError(response.error || "Failed to load the letter");
      }
      setIsLoadingContent(false);
    });

    return () => {
      cancelled = true;
    };
  }, [selectedChatId, isSelectedLocked]);

  const handleDownload = async (format: AppealLetterFormat) => {
    if (downloadingFormat || selectedChatId === null || isSelectedLocked)
      return;
    setDownloadingFormat(format);
    try {
      // No version: the download endpoint serves the chat's latest, like the view
      const result = await saveAppealLetter(
        selectedChatId,
        format,
        claimNumber,
      );
      if (!result.success) {
        notifier.showError(result.error ?? "Failed to download the letter");
      }
    } finally {
      setDownloadingFormat(null);
    }
  };

  if (versions.length === 0) {
    return <PrivateChatNotice letters={[letter]} />;
  }

  const latestChatId = versions[0]?.chatId ?? null;

  return (
    <Box>
      {/* Toolbar: version picker, downloads, link to the chat */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        {/* CoreSelect fills its container's width, so the wrapper sets it */}
        <Box sx={{ flex: "0 1 130px", minWidth: 130 }}>
          <CoreSelect
            size="small"
            variant="outlined"
            value={selectedChatId ?? ""}
            onChange={(e) => setSelectedChatId(String(e.target.value))}
            inputProps={{ "aria-label": "Letter version" }}
            // The theme's MuiFormControl override sets "& .MuiInputBase-root" to 48px, and its
            // descendant selector outranks the Select's own sx, so override it at the same level
            formControlProps={{
              sx: {
                "& .MuiInputBase-root": { height: 30, fontSize: "12px" },
                "& .MuiSelect-select": { py: 0, fontSize: "12px" },
              },
            }}
            options={versions.map((version) => ({
              value: version.chatId,
              label: `Version ${version.number}${version.chatId === latestChatId ? " (latest)" : ""}${version.available ? "" : " (private)"}`,
            }))}
          />
        </Box>

        <Box sx={{ flex: 1 }} />

        {!isSelectedLocked && (
          <Tooltip title="View chat">
            <Box
              component="button"
              type="button"
              aria-label="View chat"
              onClick={() =>
                navigate(`/channels/${selectedChatId ?? letter.chatId}`)
              }
              className="flex items-center justify-center rounded-[8px] w-[30px] h-[30px] hover:bg-gray-100"
              sx={{ color: "custom.brandTeal" }}
            >
              <RiLinkM size={18} />
            </Box>
          </Tooltip>
        )}

        {canDownloadAttachments && !isSelectedLocked && (
          <>
            {(["docx", "pdf"] as AppealLetterFormat[]).map((format) => (
              <button
                key={format}
                type="button"
                onClick={() => handleDownload(format)}
                disabled={!!downloadingFormat}
                className="flex items-center gap-1 rounded-[6px] px-1.5 h-[24px] text-[0.75rem] text-gray-700 border border-gray-200 hover:bg-gray-100 disabled:opacity-50"
                aria-label={`Download as ${format.toUpperCase()}`}
                aria-busy={downloadingFormat === format}
              >
                {downloadingFormat === format ? (
                  <CircularProgress size={12} color="inherit" />
                ) : (
                  <DownloadIcon size={12} />
                )}
                {format.toUpperCase()}
              </button>
            ))}
          </>
        )}
      </Box>

      {/* Letter body, read-only */}
      {isSelectedLocked ? (
        <PrivateChatNotice
          letters={selectedVersion ? [selectedVersion] : [letter]}
        />
      ) : isLoadingContent ? (
        <Box sx={{ p: 2 }}>
          <Skeleton height={20} sx={{ mb: 1 }} />
          <Skeleton height={20} sx={{ mb: 1 }} />
          <Skeleton height={20} sx={{ mb: 1 }} />
          <Skeleton height={100} />
        </Box>
      ) : error ? (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography color="error">{error}</Typography>
        </Box>
      ) : (
        // Fixed height: the claims panel scrolls, and the editor sizes itself to its container
        <Box
          data-testid="appeal-letter-content"
          sx={{ height: "calc(100vh - 325px)", minHeight: 400 }}
        >
          {/* No onChange/onSave, so nothing typed here is ever persisted */}
          <DocumentEditor
            content={content ?? ""}
            hideHeader
            disableEditMode={true}
            claimId={claimNumber}
          />
        </Box>
      )}
    </Box>
  );
};

export default AppealLetterDetail;
